import { useEffect, useMemo, useRef, useState } from 'react';
import { Printer, Trash2, Plus, Search, RotateCcw } from 'lucide-react';
import { hidratarAdiantamentoMdf, salvarAdiantamentoMdf } from '../../utils/adiantamentoMdfPersistencia';
import {
  FUNCOES_ADIANTAMENTO_MDF,
  chavePecaAdiantamentoMdf,
  diaIsoDeDataHora,
  estadoAdiantamentoMdfVazio,
  estadoProdutoAdiantamentoVazio,
  pecaMaterialAdiantavel,
  pecaMaterialEhMdf,
  type EstadoAdiantamentoMdf,
  type EstadoProdutoAdiantamentoMdf,
  type ExtraAdiantamentoMdf,
  type FuncaoAdiantamentoMdf,
  type PecaAdiantamentoMdf,
} from '../../utils/adiantamentoMdfTipos';
import {
  chaveGrupoVarianteAdiantamento,
  chaveLinhaUnificadaAdiantamento,
  materialChaveAdiantamento,
  rotuloVariantesAdiantamento,
} from '../../utils/adiantamentoMdfUnificar';
import { nomesProdutoIguais } from '../../utils/autoCardsTempoCorte';
import { descricaoPecaSemCor, nomeProdutoSemCor } from '../../utils/resolverProdutoPontuacao';

interface ItemTc {
  id?: string;
  descricao?: string;
  material?: string;
}

interface ProdutoTc {
  id?: string;
  nome?: string;
  lote?: string;
  itens?: ItemTc[];
  dataHoraInicioExato?: string;
  dataHoraInicioProducao?: string;
}

interface LoteTc {
  lote?: string;
  produtoNome?: string;
  dataHoraInicioCorte?: string;
}

interface FonteLinha {
  produtoId: string;
  itemId: string;
  produtoNome: string;
  origem: 'tc' | 'extra';
}

interface LinhaUnificada {
  chave: string;
  tituloProduto: string;
  descricao: string;
  material: string;
  funcao: FuncaoAdiantamentoMdf | '';
  fontes: FonteLinha[];
  dataAdiantamento: string;
}

const ST_PRODUTO_VAZIO = estadoProdutoAdiantamentoVazio();

interface Props {
  produtosTempoCorte: ProdutoTc[];
  lotesProdutos: LoteTc[];
  onAlterarDescricaoPeca: (produtoId: string, itemId: string, novaDescricao: string) => void;
}

function escaparHtml(s: string): string {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function dataInicioProduto(p: ProdutoTc, lotes: LoteTc[]): string {
  const loteN = String(p.lote || '').trim().toLowerCase();
  const nome = String(p.nome || '');
  const doLote = lotes.find(
    (l) =>
      String(l.lote || '').trim().toLowerCase() === loteN &&
      loteN !== '' &&
      nomesProdutoIguais(String(l.produtoNome || ''), nome)
  )?.dataHoraInicioCorte;
  return diaIsoDeDataHora(p.dataHoraInicioExato || p.dataHoraInicioProducao || doLote);
}

function dataBrCurta(iso: string): string {
  if (!iso) return 'S/DATA';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return iso;
  return `${m[3]}/${m[2]}`;
}

function textoPecaCompacta(desc: string, material: string): string {
  const d = String(desc || '').trim();
  const mat = String(material || '')
    .replace(/\s*-\s*/g, ' - ')
    .replace(/\s+/g, ' ')
    .trim();
  if (d && mat) return `${d} - ${mat}`;
  return d || mat;
}

function juntarComE(itens: string[]): string {
  const limpos = itens.map((s) => s.trim()).filter(Boolean);
  if (limpos.length === 0) return '';
  if (limpos.length === 1) return limpos[0];
  if (limpos.length === 2) return `${limpos[0]} e ${limpos[1]}`;
  return `${limpos.slice(0, -1).join(' - ')} e ${limpos[limpos.length - 1]}`;
}

function linhaCompactaDoDia(linhasDoDia: LinhaUnificada[], dataIso: string): string {
  const porProduto = new Map<string, LinhaUnificada[]>();
  for (const l of linhasDoDia) {
    const arr = porProduto.get(l.tituloProduto) || [];
    arr.push(l);
    porProduto.set(l.tituloProduto, arr);
  }
  const blocos = [...porProduto.entries()].map(([titulo, pecas]) => {
    const textos = pecas.map((p) => textoPecaCompacta(p.descricao, p.material));
    return `${juntarComE(textos)} do ${titulo}`;
  });
  return `${dataBrCurta(dataIso)} ${blocos.join(', ')}`;
}

function funcaoDaLinha(l: LinhaUnificada): FuncaoAdiantamentoMdf {
  return l.funcao && FUNCOES_ADIANTAMENTO_MDF.some((f) => f.id === l.funcao) ? l.funcao : 'corte';
}

function imprimirListaUnificada(linhas: LinhaUnificada[]): void {
  const porFuncao = new Map<string, Map<string, LinhaUnificada[]>>();
  for (const l of linhas) {
    const fn = funcaoDaLinha(l);
    let porData = porFuncao.get(fn);
    if (!porData) {
      porData = new Map();
      porFuncao.set(fn, porData);
    }
    const d = l.dataAdiantamento || '';
    const arr = porData.get(d) || [];
    arr.push(l);
    porData.set(d, arr);
  }

  const blocos = FUNCOES_ADIANTAMENTO_MDF.map((f) => {
    const porData = porFuncao.get(f.id);
    if (!porData || porData.size === 0) return '';
    const datas = [...porData.keys()].sort((a, b) => {
      if (!a) return 1;
      if (!b) return -1;
      return a.localeCompare(b);
    });
    const paragrafos = datas
      .map((dataIso) => {
        const texto = linhaCompactaDoDia(porData.get(dataIso) || [], dataIso);
        return `<p>${escaparHtml(texto)}</p>`;
      })
      .join('');
    return `<section><h2>${escaparHtml(f.label)}</h2>${paragrafos}</section>`;
  }).filter(Boolean);

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8" />
  <title>Adiantamento de MDF</title>
  <style>
    @page { size: A4 portrait; margin: 8mm; }
    body { font-family: Arial, sans-serif; color: #111; font-size: 9pt; line-height: 1.35; }
    h1 { font-size: 13pt; margin: 0 0 3mm; }
    h2 { font-size: 11pt; margin: 3.5mm 0 1.5mm; border-bottom: 1px solid #222; padding-bottom: 0.8mm; text-transform: uppercase; }
    p { margin: 0 0 1.6mm; text-align: justify; }
    section { page-break-inside: avoid; }
  </style>
</head>
<body>
  <h1>Adiantamento de MDF</h1>
  ${blocos.join('') || '<p>Nenhuma peça para imprimir.</p>'}
</body>
</html>`;
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => win.print(), 300);
}

function pecasDeProduto(p: ProdutoTc, estado: EstadoAdiantamentoMdf): PecaAdiantamentoMdf[] {
  const pid = String(p.id || '');
  const st = estado.porProduto[pid] || ST_PRODUTO_VAZIO;
  const ocultos = new Set(st.ocultos);
  const incluidos = new Set(st.incluidos);
  const itens = Array.isArray(p.itens) ? p.itens : [];
  const out: PecaAdiantamentoMdf[] = [];
  for (const it of itens) {
    const id = String(it.id || '');
    if (!id) continue;
    const mostrar = (pecaMaterialEhMdf(it.material) && !ocultos.has(id)) || incluidos.has(id);
    if (!mostrar) continue;
    out.push({
      itemId: id,
      produtoId: pid,
      produtoNome: nomeProdutoSemCor(String(p.nome || '')),
      lote: String(p.lote || ''),
      descricao: descricaoPecaSemCor(String(it.descricao || '')),
      material: String(it.material || ''),
      funcao: estado.funcoesPorPeca[chavePecaAdiantamentoMdf(pid, it)] || '',
      origem: 'tc',
    });
  }
  for (const ex of st.extras) {
    out.push({
      itemId: ex.id,
      produtoId: pid,
      produtoNome: nomeProdutoSemCor(String(p.nome || '')),
      lote: String(p.lote || ''),
      descricao: ex.descricao,
      material: ex.material,
      funcao: ex.funcao || estado.funcoesPorPeca[chavePecaAdiantamentoMdf(pid, ex)] || '',
      origem: 'extra',
    });
  }
  return out;
}

function unificarPecas(
  pecas: PecaAdiantamentoMdf[],
  datasPorLinha: Record<string, string>
): LinhaUnificada[] {
  const map = new Map<string, LinhaUnificada>();
  for (const p of pecas) {
    const grupo = chaveGrupoVarianteAdiantamento(p.produtoNome);
    const desc = descricaoPecaSemCor(p.descricao).toUpperCase();
    const mat = materialChaveAdiantamento(p.material) || String(p.material || '').toUpperCase();
    const chave = chaveLinhaUnificadaAdiantamento(grupo, desc, mat);
    const atual = map.get(chave);
    if (!atual) {
      map.set(chave, {
        chave,
        tituloProduto: p.produtoNome,
        descricao: desc,
        material: mat,
        funcao: p.funcao,
        fontes: [
          {
            produtoId: p.produtoId,
            itemId: p.itemId,
            produtoNome: p.produtoNome,
            origem: p.origem,
          },
        ],
        dataAdiantamento: datasPorLinha[chave] || '',
      });
      continue;
    }
    if (!atual.fontes.some((f) => f.produtoId === p.produtoId && f.itemId === p.itemId)) {
      atual.fontes.push({
        produtoId: p.produtoId,
        itemId: p.itemId,
        produtoNome: p.produtoNome,
        origem: p.origem,
      });
    }
    if (!atual.funcao && p.funcao) atual.funcao = p.funcao;
  }
  for (const linha of map.values()) {
    linha.tituloProduto = rotuloVariantesAdiantamento(linha.fontes.map((f) => f.produtoNome));
  }
  return [...map.values()].sort((a, b) => {
    const da = a.dataAdiantamento || '9999-99-99';
    const db = b.dataAdiantamento || '9999-99-99';
    if (da !== db) return da.localeCompare(db);
    const t = a.tituloProduto.localeCompare(b.tituloProduto, 'pt-BR');
    if (t !== 0) return t;
    return a.descricao.localeCompare(b.descricao, 'pt-BR');
  });
}

export default function AdiantamentoMdfSection({
  produtosTempoCorte,
  lotesProdutos,
  onAlterarDescricaoPeca,
}: Props) {
  const [estado, setEstado] = useState<EstadoAdiantamentoMdf>(estadoAdiantamentoMdfVazio);
  const [produtoFocoId, setProdutoFocoId] = useState('');
  const [busca, setBusca] = useState('');
  const [novaDesc, setNovaDesc] = useState('');
  const [novoMaterial, setNovoMaterial] = useState('MDF');
  const [incluirItemId, setIncluirItemId] = useState('');
  const prontoParaSalvar = useRef(false);

  useEffect(() => {
    let cancelado = false;
    void (async () => {
      const e = await hidratarAdiantamentoMdf();
      if (cancelado) return;
      setEstado(e);
      prontoParaSalvar.current = true;
    })();
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    if (!prontoParaSalvar.current) return;
    const t = window.setTimeout(() => {
      void salvarAdiantamentoMdf(estado);
    }, 400);
    return () => window.clearTimeout(t);
  }, [estado]);

  const dataFiltro = estado.dataInicioFiltro;

  const produtosDoDia = useMemo(() => {
    if (!dataFiltro) return [];
    const q = busca.trim().toUpperCase();
    return [...produtosTempoCorte]
      .filter((p) => {
        const dia = dataInicioProduto(p, lotesProdutos);
        return Boolean(dia) && dia >= dataFiltro;
      })
      .filter((p) => {
        if (!q) return true;
        return `${p.nome || ''} ${p.lote || ''}`.toUpperCase().includes(q);
      })
      .sort((a, b) => String(a.nome || '').localeCompare(String(b.nome || ''), 'pt-BR'));
  }, [produtosTempoCorte, lotesProdutos, dataFiltro, busca]);

  const produtosNaLista = useMemo(() => {
    const ids = estado.produtoIdsNaLista;
    return ids
      .map((id) => produtosTempoCorte.find((p) => String(p.id) === id))
      .filter((p): p is ProdutoTc => Boolean(p));
  }, [estado.produtoIdsNaLista, produtosTempoCorte]);

  const produtoFoco = useMemo(
    () => produtosNaLista.find((p) => String(p.id) === produtoFocoId) ?? null,
    [produtosNaLista, produtoFocoId]
  );

  useEffect(() => {
    if (produtoFocoId && !produtosNaLista.some((p) => String(p.id) === produtoFocoId)) {
      setProdutoFocoId(produtosNaLista[0] ? String(produtosNaLista[0].id) : '');
    }
  }, [produtosNaLista, produtoFocoId]);

  const stFoco: EstadoProdutoAdiantamentoMdf =
    (produtoFocoId && estado.porProduto[produtoFocoId]) || ST_PRODUTO_VAZIO;

  const patchProduto = (
    produtoIdAlvo: string,
    fn: (s: EstadoProdutoAdiantamentoMdf) => EstadoProdutoAdiantamentoMdf
  ) => {
    setEstado((prev) => {
      const atual = prev.porProduto[produtoIdAlvo] || estadoProdutoAdiantamentoVazio();
      return {
        ...prev,
        porProduto: { ...prev.porProduto, [produtoIdAlvo]: fn(atual) },
      };
    });
  };

  const toggleProdutoNaLista = (id: string, incluir: boolean) => {
    setEstado((prev) => {
      const set = new Set(prev.produtoIdsNaLista);
      if (incluir) set.add(id);
      else set.delete(id);
      return { ...prev, produtoIdsNaLista: [...set] };
    });
    if (incluir) setProdutoFocoId(id);
  };

  const incluirTodosVisiveis = () => {
    setEstado((prev) => {
      const set = new Set(prev.produtoIdsNaLista);
      for (const p of produtosDoDia) {
        const id = String(p.id || '');
        if (id) set.add(id);
      }
      return { ...prev, produtoIdsNaLista: [...set] };
    });
  };

  const itensTcFoco = useMemo(
    () => (Array.isArray(produtoFoco?.itens) ? produtoFoco.itens : []),
    [produtoFoco]
  );

  const pecasFocoVisiveisIds = useMemo(() => {
    if (!produtoFocoId) return new Set<string>();
    const ocultos = new Set(stFoco.ocultos);
    const incluidos = new Set(stFoco.incluidos);
    const ids = new Set<string>();
    for (const it of itensTcFoco) {
      const id = String(it.id || '');
      if (!id) continue;
      if ((pecaMaterialEhMdf(it.material) && !ocultos.has(id)) || incluidos.has(id)) ids.add(id);
    }
    return ids;
  }, [produtoFocoId, stFoco, itensTcFoco]);

  const pecasParaIncluir = useMemo(() => {
    if (!produtoFocoId) return [];
    return itensTcFoco.filter((it) => {
      const id = String(it.id || '');
      if (!id || pecasFocoVisiveisIds.has(id)) return false;
      return pecaMaterialAdiantavel(it.material);
    });
  }, [itensTcFoco, pecasFocoVisiveisIds, produtoFocoId]);

  const linhas = useMemo(() => {
    const pecas: PecaAdiantamentoMdf[] = [];
    for (const p of produtosNaLista) pecas.push(...pecasDeProduto(p, estado));
    return unificarPecas(pecas, estado.datasPorLinha);
  }, [produtosNaLista, estado]);

  const definirFuncaoLinha = (linha: LinhaUnificada, funcao: FuncaoAdiantamentoMdf | '') => {
    setEstado((prev) => {
      const funcoes = { ...prev.funcoesPorPeca };
      const porProduto = { ...prev.porProduto };
      for (const f of linha.fontes) {
        const k = chavePecaAdiantamentoMdf(f.produtoId, { id: f.itemId });
        funcoes[k] = funcao;
        if (f.origem === 'extra') {
          const atual = porProduto[f.produtoId] || estadoProdutoAdiantamentoVazio();
          porProduto[f.produtoId] = {
            ...atual,
            extras: atual.extras.map((x) => (x.id === f.itemId ? { ...x, funcao } : x)),
          };
        }
      }
      return { ...prev, funcoesPorPeca: funcoes, porProduto };
    });
  };

  const definirDataLinha = (chave: string, data: string) => {
    setEstado((prev) => ({
      ...prev,
      datasPorLinha: { ...prev.datasPorLinha, [chave]: data },
    }));
  };

  const excluirLinha = (linha: LinhaUnificada) => {
    setEstado((prev) => {
      const porProduto = { ...prev.porProduto };
      const datas = { ...prev.datasPorLinha };
      delete datas[linha.chave];
      for (const f of linha.fontes) {
        const atual = porProduto[f.produtoId] || estadoProdutoAdiantamentoVazio();
        if (f.origem === 'extra') {
          porProduto[f.produtoId] = {
            ...atual,
            extras: atual.extras.filter((x) => x.id !== f.itemId),
          };
        } else {
          porProduto[f.produtoId] = {
            ...atual,
            ocultos: atual.ocultos.includes(f.itemId) ? atual.ocultos : [...atual.ocultos, f.itemId],
            incluidos: atual.incluidos.filter((x) => x !== f.itemId),
          };
        }
      }
      return { ...prev, porProduto, datasPorLinha: datas };
    });
  };

  const alterarDescricaoLinha = (linha: LinhaUnificada, valor: string) => {
    const limpo = descricaoPecaSemCor(valor).toUpperCase();
    for (const f of linha.fontes) {
      if (f.origem === 'extra') {
        patchProduto(f.produtoId, (s) => ({
          ...s,
          extras: s.extras.map((x) => (x.id === f.itemId ? { ...x, descricao: limpo } : x)),
        }));
      } else {
        onAlterarDescricaoPeca(f.produtoId, f.itemId, limpo);
      }
    }
  };

  const restaurarOcultosFoco = () => {
    if (!produtoFocoId) return;
    patchProduto(produtoFocoId, (s) => ({ ...s, ocultos: [] }));
  };

  const incluirPecaDoProduto = () => {
    if (!produtoFocoId || !incluirItemId) return;
    patchProduto(produtoFocoId, (s) => ({
      ...s,
      ocultos: s.ocultos.filter((x) => x !== incluirItemId),
      incluidos: s.incluidos.includes(incluirItemId) ? s.incluidos : [...s.incluidos, incluirItemId],
    }));
    setIncluirItemId('');
  };

  const adicionarPecaManual = () => {
    if (!produtoFocoId) {
      alert('Marque um produto na lista para vincular a peça nova.');
      return;
    }
    const desc = descricaoPecaSemCor(novaDesc).toUpperCase();
    if (!desc) {
      alert('Informe a descrição da peça.');
      return;
    }
    const extra: ExtraAdiantamentoMdf = {
      id: `ex-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`,
      descricao: desc,
      material: String(novoMaterial || 'MDF').toUpperCase(),
      funcao: '',
    };
    patchProduto(produtoFocoId, (s) => ({ ...s, extras: [...s.extras, extra] }));
    setNovaDesc('');
  };

  const idsNaLista = new Set(estado.produtoIdsNaLista);

  return (
    <div className="space-y-4">
      <div>
        <h3 className="font-semibold text-black text-lg">Adiantamento de MDF</h3>
        <p className="text-sm text-neutral-600 mt-1">
          A data <strong>a partir de</strong> só filtra quais produtos você pode incluir. Marque
          vários produtos na lista. Peças iguais de variantes (ex.: Painel Estone 1.4 e 1.8 com
          PRAT SUP) viram uma linha só. Em cada linha coloque o <strong>dia de adiantar</strong>.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-xs font-medium text-neutral-600 mb-1">
            Buscar produtos a partir de
          </label>
          <input
            type="date"
            value={dataFiltro}
            onChange={(e) =>
              setEstado((prev) => ({ ...prev, dataInicioFiltro: e.target.value }))
            }
            className="w-full px-3 py-2 rounded-lg border border-[#009b3a]/22 text-sm font-semibold"
          />
        </div>
        <div className="md:col-span-2 relative">
          <label className="block text-xs font-medium text-neutral-600 mb-1">Filtrar nome/lote</label>
          <Search className="w-4 h-4 absolute left-3 top-8 text-neutral-400" />
          <input
            type="search"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            disabled={!dataFiltro}
            placeholder={dataFiltro ? 'Filtrar produto ou lote...' : 'Escolha a data primeiro'}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-[#009b3a]/22 text-sm"
          />
        </div>
      </div>

      {!dataFiltro ? (
        <p className="text-sm text-neutral-600 text-center py-6">
          Informe a data para listar os produtos que podem entrar.
        </p>
      ) : produtosDoDia.length === 0 ? (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Nenhum produto salvo no Tempo de Corte com início nesta data ou depois.
        </p>
      ) : (
        <div className="rounded-xl border border-[#009b3a]/22 bg-[#fafbfc] p-3">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <p className="text-sm font-semibold text-black">
              Produtos para incluir ({idsNaLista.size} na lista)
            </p>
            <button
              type="button"
              onClick={incluirTodosVisiveis}
              className="px-3 py-1.5 rounded-lg border border-[#009b3a]/35 bg-[#e8f5ec] text-[#006b28] text-xs font-semibold"
            >
              Incluir todos visíveis
            </button>
          </div>
          <div className="max-h-40 overflow-y-auto space-y-1 text-sm">
            {produtosDoDia.map((p) => {
              const id = String(p.id || '');
              const naLista = idsNaLista.has(id);
              return (
                <label key={id} className="flex items-center gap-2 cursor-pointer py-0.5">
                  <input
                    type="checkbox"
                    checked={naLista}
                    onChange={(e) => toggleProdutoNaLista(id, e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className={naLista ? 'font-semibold' : ''}>
                    {nomeProdutoSemCor(p.nome || '(sem nome)')}
                    {p.lote ? ` · lote ${p.lote}` : ''}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {produtosNaLista.length > 0 && (
        <>
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">
                Produto para peça extra
              </label>
              <select
                value={produtoFocoId}
                onChange={(e) => setProdutoFocoId(e.target.value)}
                className="px-3 py-2 rounded-lg border border-[#009b3a]/22 text-sm font-semibold min-w-[220px]"
              >
                {produtosNaLista.map((p) => (
                  <option key={String(p.id)} value={String(p.id)}>
                    {nomeProdutoSemCor(p.nome || '')}
                  </option>
                ))}
              </select>
            </div>
            {pecasParaIncluir.length > 0 && (
              <>
                <select
                  value={incluirItemId}
                  onChange={(e) => setIncluirItemId(e.target.value)}
                  className="px-3 py-2 rounded-lg border border-[#009b3a]/22 text-sm min-w-[220px]"
                >
                  <option value="">Incluir MDF/aglomerado…</option>
                  {pecasParaIncluir.map((it) => (
                    <option key={String(it.id)} value={String(it.id)}>
                      {descricaoPecaSemCor(String(it.descricao || '—')).toUpperCase()} ·{' '}
                      {String(it.material || '').toUpperCase()}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={incluirPecaDoProduto}
                  className="px-3 py-2 rounded-lg border border-[#1F4E78]/40 bg-[#e8eef5] text-[#1F4E78] text-sm font-semibold"
                >
                  Incluir peça
                </button>
              </>
            )}
            {stFoco.ocultos.length > 0 && (
              <button
                type="button"
                onClick={restaurarOcultosFoco}
                className="px-3 py-2 rounded-lg border border-neutral-300 bg-[#eef0f4] text-sm font-semibold flex items-center gap-1"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Restaurar {stFoco.ocultos.length} ocultas
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (linhas.length === 0) {
                  alert('A lista está vazia.');
                  return;
                }
                imprimirListaUnificada(linhas);
              }}
              className="px-4 py-2 ade-btn-gold relative z-10 font-semibold rounded-lg text-sm flex items-center gap-2 ml-auto"
            >
              <Printer className="w-4 h-4" />
              Imprimir lista
            </button>
          </div>

          <div className="flex flex-wrap gap-2 items-end rounded-xl border border-[#009b3a]/22 bg-[#fafbfc] px-3 py-2">
            <div>
              <label className="block text-xs font-medium text-neutral-600 mb-1">Nova peça</label>
              <input
                type="text"
                value={novaDesc}
                onChange={(e) => setNovaDesc(e.target.value.toUpperCase())}
                placeholder="Descrição"
                className="px-3 py-2 rounded-lg border border-[#009b3a]/22 text-sm w-64 uppercase"
              />
            </div>
            <select
              value={novoMaterial}
              onChange={(e) => setNovoMaterial(e.target.value)}
              className="px-3 py-2 rounded-lg border border-[#009b3a]/22 text-sm"
            >
              <option value="MDF">MDF</option>
              <option value="AGLOMERADO">AGLOMERADO</option>
            </select>
            <button
              type="button"
              onClick={adicionarPecaManual}
              className="px-3 py-2 rounded-lg border border-[#009b3a]/35 bg-[#e8f5ec] text-[#006b28] text-sm font-semibold flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Adicionar
            </button>
          </div>

          {linhas.length === 0 ? (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Os produtos estão na lista, mas não há peça visível. Inclua MDF/aglomerado ou uma
              peça nova.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-[#009b3a]/22">
              <table className="w-full text-xs border-collapse">
                <thead className="bg-[#e4e6ec]">
                  <tr>
                    <th className="px-2 py-2 border text-left">Produto</th>
                    <th className="px-2 py-2 border text-left">Descrição</th>
                    <th className="px-2 py-2 border text-center">Material</th>
                    <th className="px-2 py-2 border text-left">Função</th>
                    <th className="px-2 py-2 border text-center">Data adiantar</th>
                    <th className="px-2 py-2 border text-center w-16">Excluir</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((linha) => (
                    <tr key={linha.chave} className="hover:bg-[#f0faf3]">
                      <td className="px-2 py-1.5 border font-semibold text-[#006b28]">
                        {linha.tituloProduto}
                      </td>
                      <td className="px-2 py-1.5 border">
                        <input
                          type="text"
                          value={linha.descricao}
                          onChange={(e) => alterarDescricaoLinha(linha, e.target.value)}
                          className="w-full px-1 py-1 text-xs border rounded uppercase font-medium"
                        />
                      </td>
                      <td className="px-2 py-1.5 border text-center uppercase">{linha.material || '—'}</td>
                      <td className="px-2 py-1.5 border">
                        <select
                          value={linha.funcao}
                          onChange={(e) =>
                            definirFuncaoLinha(
                              linha,
                              (e.target.value || '') as FuncaoAdiantamentoMdf | ''
                            )
                          }
                          className="w-full px-2 py-1 rounded border border-[#009b3a]/22 bg-white"
                        >
                          <option value="">— escolher —</option>
                          {FUNCOES_ADIANTAMENTO_MDF.map((f) => (
                            <option key={f.id} value={f.id}>
                              {f.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-2 py-1.5 border text-center">
                        <input
                          type="date"
                          value={linha.dataAdiantamento}
                          onChange={(e) => definirDataLinha(linha.chave, e.target.value)}
                          className="px-1 py-1 rounded border border-[#009b3a]/22 text-xs"
                        />
                      </td>
                      <td className="px-2 py-1.5 border text-center">
                        <button
                          type="button"
                          onClick={() => excluirLinha(linha)}
                          className="text-red-600 hover:text-red-800 inline-flex"
                          title="Tirar desta lista"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
